// ============================================================
// AppHeader.tsx
// Shared top-nav header used by ViewingPane, CaseListScreen, and
// Settings. Owns the wordmark, subtitle, logo SVG, and outer
// nav layout. Each consumer supplies its own right-side controls
// (children) and optionally a left-side accessory (leftSlot).
//
// Pure refactor of three identical-looking headers — props exist
// to preserve pixel-level differences between the original
// hand-written copies:
//
//  compactLogoOnMobile  ViewingPane shrinks the logo on mobile to
//                       make room for its "← Cases" button.
//  hideSubtitleOnMobile  ViewingPane and CaseListScreen hide the
//                       "Case Management" subtitle on small
//                       screens; Settings keeps it always.
// ============================================================
import React, { ReactNode, useContext } from 'react'
import { createPortal } from 'react-dom'
import { useCasevoStore } from '../../store/useCasevoStore'
import UserAvatar from '../shared/UserAvatar'
import NotificationsBell from '../notifications/NotificationsBell'
import RailTooltip from '../shared/RailTooltip'
import { HeaderPortalContext } from './HeaderPortalContext'
import { navigateTo } from '../../navigation/navigate'

interface AppHeaderProps {
  /** Right-side action slot (bell, settings link, sign out, etc.) */
  children?: ReactNode
  /** Click handler for the wordmark. When omitted, the wordmark
   *  renders as static text (e.g. on the auth screens). */
  onWordmarkClick?: () => void | Promise<void>
  /** Optional content rendered before the logo (e.g. ← Cases / ← Back). */
  leftSlot?: ReactNode
  /** Optional content rendered BEFORE leftSlot — used by the global
   *  sidebar's mobile hamburger trigger. Hidden on desktop by the
   *  caller (md:hidden), so on desktop this slot is effectively empty. */
  leftHamburger?: ReactNode
  /** ViewingPane-only: 24px on mobile / 28px on sm+; tighter gap. */
  compactLogoOnMobile?: boolean
  /** ViewingPane and CaseListScreen hide the subtitle on mobile. */
  hideSubtitleOnMobile?: boolean
  /** Drop the header's light bottom border (keeps the 1px for layout, but
   *  transparent). Used by the Calendar in DARK focus mode: the header sits
   *  OUTSIDE the calendar's theme wrapper, so in an otherwise-LIGHT app its
   *  light `border-casevo-border` draws a white seam against the dark diary
   *  below. Transparent here removes that seam; the dark-green header meets
   *  the dark diary cleanly. Omitted/false everywhere else → border unchanged. */
  hideBottomBorder?: boolean
}

const AppHeader: React.FC<AppHeaderProps> = ({
  children,
  onWordmarkClick,
  leftSlot,
  leftHamburger,
  hideBottomBorder = false,
  // compactLogoOnMobile + hideSubtitleOnMobile are intentionally NOT
  // destructured even though they're still declared on the interface
  // (so existing call sites compile without churn). The behaviour the
  // two props once selected is now the default for every consumer —
  // see the innerGapClass / subtitleClass docblock below. Leaving them
  // off the destructure avoids the @typescript-eslint/no-unused-vars
  // lint failure on the CI build.
}) => {
  // Read authUser so the right-edge avatar reflects the
  // current user's saved colour. Subscribing via selector (not
  // getState()) so a colour change in Settings re-renders the
  // header avatar immediately. authUser is null on the unauth
  // path — guard below so AppHeader stays usable when called
  // from auth screens (they don't actually render this header
  // today, but the guard keeps the component honest).
  const authUser = useCasevoStore((s) => s.authUser)
  // Avatar-click → open Settings. Replicates Sidebar.tsx's handleSettings
  // sequence VERBATIM — flush every outstanding auto-save first, then
  // clear sibling takeovers in App.tsx's render-precedence order, then
  // flip showSettings true. Identical reliability + no-data-loss
  // guarantees, just triggered from the header avatar instead of the
  // sidebar nav row. setSidebarMobileOpen is intentionally OMITTED —
  // that flag is Sidebar-local state AppHeader doesn't subscribe to and
  // doesn't need to mutate (the sidebar handles its own dismiss when
  // App.tsx re-renders with showSettings true if it cares).
  const handleOpenSettings = async (): Promise<void> => {
    // Land Settings on the Account section (identity card + "Change
    // avatar") — the avatar is the reason someone clicks their own PFP.
    // navigateTo flushes autosaves, asks the unsaved-changes guard and
    // records the step in history (Back returns here).
    await navigateTo({ view: 'settings', params: { section: 'account' } })
  }
  // Mobile-tightening defaults (previously gated behind the
  // compactLogoOnMobile / hideSubtitleOnMobile props on a per-caller
  // basis): the row was crammed on narrow screens for callers that
  // didn't pass either prop. Now the mobile-compact behaviour is the
  // default for every consumer:
  //   - innerGapClass: 8 px gap on mobile, 12 px sm:+ (matches the
  //     previous compactLogoOnMobile=true value). The floor is gap-2.
  //   - subtitleClass: always carries `hidden sm:block` so the
  //     "Case Management" subtitle never paints below the sm
  //     breakpoint, regardless of the prop.
  // The two props remain on the interface so existing call sites keep
  // compiling without churn; they're no longer read here. A future
  // sweep could drop them entirely.
  const innerGapClass = 'gap-2 sm:gap-3'
  const subtitleClass = 'text-casevo-light text-xs tracking-widest uppercase leading-none hidden sm:block'

  // Wordmark is a real <button> when interactive (gets focus ring +
  // hover) and a static <span> otherwise. The interactive variant's
  // class string mirrors the previous hand-written copies exactly.
  const wordmark = onWordmarkClick ? (
    <button
      type="button"
      onClick={onWordmarkClick}
      className="text-white font-medium text-sm tracking-widest uppercase leading-none
                 bg-transparent border-0 p-0 m-0 cursor-pointer text-left
                 hover:text-casevo-light transition-colors
                 focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-light rounded-sm"
    >
      Casevo
    </button>
  ) : (
    <span className="text-white font-medium text-sm tracking-widest uppercase leading-none">
      Casevo
    </span>
  )

  const portalEl = useContext(HeaderPortalContext)
  // Layout B: on DESKTOP the topbar has NO bottom border (md:border-b-0) so it
  // flows seamlessly into the deep-forest sidebar below-left — one continuous
  // chrome, no dividing line at the junction. Separation from the CONTENT there
  // comes from the topbar container's drop shadow (App.tsx), not a hard line. On
  // MOBILE (no sidebar under the topbar) the border-b is kept for topbar↔content
  // separation, since the desktop shadow is md:-gated. hideBottomBorder
  // (Calendar dark focus) still forces it transparent.
  const header = (
    <nav className={`w-full z-40 bg-casevo-header px-4 sm:px-6 md:pl-4 h-16 sm:h-[68px] flex items-center justify-between border-b md:border-b-0 ${hideBottomBorder ? 'border-transparent' : 'border-casevo-border'}`}>
      {/* min-w-0 lets the LEFT cluster yield/truncate on a narrow viewport so
          the right cluster (bell + avatar) never gets crammed. */}
      <div className={`flex items-center min-w-0 ${innerGapClass}`}>
        {/* Sidebar collapse is now driven by the DIVIDER GRIP on the seam
            between the sidebar and the content (App.tsx) — Option D. The old
            topbar-left panel toggle has been removed. */}
        {leftHamburger}
        {leftSlot}
        {/* Logo — always responsive: 24 px on mobile, 28 px from sm: up.
            Was previously gated behind the compactLogoOnMobile prop on a
            per-caller basis (false → fixed 28 px even on mobile, which
            crammed the left cluster on narrow screens). The responsive
            recipe used by the prop's `true` branch is now the only
            recipe. Inner markup (circle + 2 rects) UNCHANGED. */}
        <svg width="24" height="24" viewBox="0 0 68 68" className="sm:w-7 sm:h-7 shrink-0">
          <circle cx="34" cy="34" r="34" fill="#2d6a4f"/>
          <rect x="23" y="17" width="9" height="34" rx="4.5" fill="#ffffff"/>
          <rect x="36" y="22" width="9" height="24" rx="4.5" fill="#74c69d"/>
        </svg>
        <div className="flex flex-col min-w-0">
          {wordmark}
          <span className={subtitleClass}>Case Management</span>
        </div>
      </div>
      {(children !== undefined || authUser !== null) && (
        // shrink-0 so the right cluster (notifications + save indicator +
        // avatar) keeps its natural size on narrow screens — the left cluster
        // (min-w-0 above) yields instead, so the avatar can't be compressed.
        <div className="flex items-center gap-2 sm:gap-4 shrink-0">
          {/* Notifications bell — mounted here (not per-screen) so it
              shows in the top bar on EVERY screen that renders AppHeader
              (now all of them: dashboard, case list, case detail, tasks,
              admin, settings, …). Rendered only for authenticated users
              (the bell fetches the personal activity feed). Sits FIRST in
              the right cluster so the order is bell → children ("+ New" /
              "Import") → avatar (rightmost), matching the
              documented convention below. */}
          {authUser && <NotificationsBell />}
          {children}
          {/* Right-edge user avatar — always rendered when there's
              an authenticated user. Reads the saved hex from
              authUser.avatarColour; the Avatar component falls
              back to brand green when undefined. Sits AFTER
              children so the visual order is e.g. NotificationsBell
              → "+ New" → Avatar (rightmost). */}
          {authUser && (
            // Wrapping <button> turns the avatar into a Settings shortcut
            // without altering the Avatar component itself. type="button"
            // prevents accidental form submits in any future header form
            // context. hover:opacity-80 + transition-opacity is the
            // affordance; focus-visible:ring matches the wordmark button's
            // focus ring for keyboard parity. The button is bg-transparent
            // / border-0 / p-0 so the avatar visually sits exactly where
            // it did before — only the cursor + hover differ.
            // App tooltip (not a native title) — right-aligned so it stays on screen.
            <RailTooltip enabled placement="below-end" belowEdgeOf="nav" label="Open settings" className="flex shrink-0">
            <button
              type="button"
              onClick={() => { void handleOpenSettings() }}
              aria-label="Open settings"
              className="shrink-0 inline-flex bg-transparent border-0 p-0 m-0 cursor-pointer rounded-full
                         hover:opacity-80 transition-opacity
                         focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-light"
            >
              <UserAvatar userId={authUser.id} fallbackName={`${authUser.firstName} ${authUser.lastName}`.trim() || authUser.email} size="sm" title={false} />
            </button>
            </RailTooltip>
          )}
        </div>
      )}
    </nav>
  )
  // Layout B — portal the header into the app-level full-width topbar container
  // so it spans above BOTH the Sidebar and the content. One screen (hence one
  // AppHeader) mounts at a time, so there's never a double header. Falls back to
  // inline rendering when no container is provided (backward-compatible).
  return portalEl ? createPortal(header, portalEl) : header
}

export default AppHeader
